import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  LinearProgress,
  Paper,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  School,
  TrendingUp,
  EmojiEvents,
  Schedule,
  ArrowForward,
  CheckCircle,
  MenuBook,
  Timer,
  LocalFireDepartment,
  Notifications,
  Assignment,
  Quiz
} from '@mui/icons-material';

// Mock Data
const MOCK_USER = {
  firstName: 'Alex',
  points: 1250,
  streak: 5,
  weeklyTime: '12h 30m',
  avatarUrl: ''
};

const MOCK_DEADLINES = [
  { id: 1, title: 'Algebra Quiz', course: 'Mathematics', dueIn: '2 days', type: 'quiz' },
  { id: 2, title: 'Essay: Shakespeare', course: 'English Literature', dueIn: '3 days', type: 'assignment' },
  { id: 3, title: 'Physics Lab Report', course: 'Science', dueIn: '5 days', type: 'assignment' }
];

const MOCK_ACTIVITY = [
  { id: 1, title: 'Completed "Linear Equations"', time: '2 hours ago', score: '95%', type: 'topic' },
  { id: 2, title: 'Earned "Math Whiz" Badge', time: 'Yesterday', type: 'achievement' },
  { id: 3, title: 'Submitted History Assignment', time: '2 days ago', type: 'assignment' }
];

const StudentDashboardV1: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

      {/* Welcome & Daily Focus */}
      <Box sx={{ mb: 5 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
              Good Morning, {MOCK_USER.firstName}! 👋
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Let's continue your progress. You're on a <strong>{MOCK_USER.streak} day streak!</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/student-courses')}
              sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 600, boxShadow: 2 }}
            >
              Go to My Courses
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: 1, height: '100%', bgcolor: 'primary.50' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <Schedule />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">{MOCK_USER.weeklyTime}</Typography>
                <Typography variant="body2" color="text.secondary">Time this week</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: 1, height: '100%', bgcolor: 'warning.50' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2, width: 56, height: 56 }}>
                <EmojiEvents />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">{MOCK_USER.points}</Typography>
                <Typography variant="body2" color="text.secondary">Total Points</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: 1, height: '100%', bgcolor: 'error.50' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 56, height: 56 }}>
                <LocalFireDepartment />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">{MOCK_USER.streak} Days</Typography>
                <Typography variant="body2" color="text.secondary">Current Streak</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: 1, height: '100%', bgcolor: 'success.50' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 56, height: 56 }}>
                <CheckCircle />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">92%</Typography>
                <Typography variant="body2" color="text.secondary">Avg. Quiz Score</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Left Column: Activity Feed & Recommended */}
        <Grid item xs={12} md={8}>

          {/* Recommended / Continue Learning */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              🎯 Continue Learning
            </Typography>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <Chip label="Mathematics" color="primary" size="small" />
                      <Typography variant="caption" color="text.secondary">Topic 3.4</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Solving Quadratic Equations
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You're 75% done with this topic. Finish the quiz to complete the module.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress variant="determinate" value={75} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                      <Typography variant="body2" fontWeight="bold">75%</Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{ borderRadius: 3, px: 3, whiteSpace: 'nowrap' }}
                    onClick={() => navigate('/student-courses')} // Ideally direct to topic
                  >
                    Resume
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Recent Activity */}
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Recent Activity
            </Typography>
            <Paper sx={{ borderRadius: 3 }}>
              <List>
                {MOCK_ACTIVITY.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem sx={{ py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{
                          bgcolor: item.type === 'topic' ? 'primary.main' : item.type === 'achievement' ? 'warning.main' : 'info.main'
                        }}>
                          {item.type === 'topic' && <MenuBook />}
                          {item.type === 'achievement' && <EmojiEvents />}
                          {item.type === 'assignment' && <Assignment />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.title}
                        secondary={item.time}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      {item.score && (
                        <Chip label={item.score} color="success" size="small" variant="outlined" />
                      )}
                    </ListItem>
                    {index < MOCK_ACTIVITY.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>

        </Grid>

        {/* Right Column: Deadlines & Notifications */}
        <Grid item xs={12} md={4}>
          {/* Upcoming Deadlines */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications color="warning" /> Upcoming Deadlines
            </Typography>
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <List sx={{ p: 0 }}>
                {MOCK_DEADLINES.map((item) => (
                  <ListItem key={item.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' }, py: 2 }}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}>
                        {item.type === 'quiz' ? <Quiz /> : <Assignment />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.title}
                      secondary={
                        <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                          {item.course} • <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>Due in {item.dueIn}</Box>
                        </Typography>
                      }
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                <Button size="small" color="primary" onClick={() => navigate('/student-courses')}>
                  View All Tasks
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentDashboardV1;